import { FormControl, MenuItem, Select, Stack } from '@mui/material';
import { BrandGroup } from '@provision/entity-types';
import { useCallback, useEffect, useState } from 'react';
import { Loader } from 'src/components';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { useGroupRules, UserGroupRules } from 'src/graphql';
import { useBrandGroup, useBrandGroups } from 'src/graphql/entities/brand-group';
import { useGlobalStore } from 'src/store';
import { ChannelsEmailForm } from './channels-email-form';
import { EmailFormModel } from './types';

export const ChannelsEmailView = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { brandGroups } = useBrandGroups(selectedPartnerId);
  const [brandGroup, setBrandGroup] = useState<BrandGroup | null>(null);

  useEffect(() => {
    if (!brandGroups.length) {
      return;
    }

    setBrandGroup(brandGroups[0]);
  }, [brandGroups, setBrandGroup]);

  const { updateBrandGroup } = useBrandGroup(selectedPartnerId);
  const { checkValidity } = useGroupRules();
  const canEditInternalEmail = checkValidity([
    UserGroupRules.AccessToEditChannelInternalEmail,
  ]);

  const handleSave = useCallback(
    (formValue: EmailFormModel) => {
      // The form saves every field together; the backend rejects any write carrying
      // internalEmail from a user without the rule, even when it is unchanged.
      const { internalEmail, ...withoutInternalEmail } = formValue;
      const patch = canEditInternalEmail ? formValue : withoutInternalEmail;

      setBrandGroup({
        ...brandGroup,
        ...patch,
      } as BrandGroup);
      void updateBrandGroup(patch);
    },
    [updateBrandGroup, brandGroup, canEditInternalEmail],
  );

  const handleBrandGroupChange = useCallback(
    (brandGroupId: string) => {
      const selectedBrandGroup = brandGroups.find((item) => item.id === brandGroupId);
      setBrandGroup(selectedBrandGroup!);
    },
    [brandGroups, setBrandGroup],
  );

  return brandGroups?.length ? (
    <Stack p={1} gap={2} sx={{ maxWidth: 600 }}>
      {brandGroups?.length > 1 ? (
        <FormControl
          sx={{
            width: 'max-content',
          }}
        >
          <FormFieldLabel id="settings-email-brand-group-label">Brand Group</FormFieldLabel>
          <Select
            labelId="settings-email-brand-group-label"
            value={brandGroup?.id ?? ''}
            id="settings-email-brand-group"
            onChange={(event) => {
              handleBrandGroupChange(event.target.value);
            }}
          >
            {brandGroups.map((brandGroup) => (
              <MenuItem key={brandGroup.id} value={brandGroup.id}>
                {brandGroup.brandGroupName || 'Not defined'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
      {brandGroup ? (
        <ChannelsEmailForm
          handleSave={handleSave}
          selectedBrandGroup={brandGroup}
          canEditInternalEmail={canEditInternalEmail}
        />
      ) : null}
    </Stack>
  ) : (
    <Loader />
  );
};
